import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button, Modal } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { EnhancedColumnType } from "../types";

import s from "./ColumnsOrderModal.module.css";
import { SortableItem } from "./SortableItem";

interface Props {
  columns: EnhancedColumnType<any>[];
  defaultColumns: EnhancedColumnType<any>[];
  toggleVisibility: () => void;
  updateColumns: (columns: EnhancedColumnType<any>[]) => void;
}

export const ColumnsOrderModal = ({
  toggleVisibility,
  columns: currentColumns,
  defaultColumns,
  updateColumns,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const [columns, setColumns] = useState<EnhancedColumnType<any>[]>([]);

  useEffect(() => {
    setColumns(currentColumns.map(item => ({ ...item })));
  }, [currentColumns]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = columns.findIndex(c => c.id === active.id);
    const newIndex = columns.findIndex(c => c.id === over.id);

    const reordered = arrayMove(columns, oldIndex, newIndex).map(
      (item, idx) => ({
        ...item,
        order: idx,
      }),
    );
    setColumns(reordered);
  };

  const handleToggleVisibility = (
    checked: boolean,
    column: EnhancedColumnType<any>,
  ) => {
    setColumns(prev =>
      prev.map(item =>
        item.id === column.id ? { ...item, visible: checked } : item,
      ),
    );
  };

  const handleDefault = () => {
    const reset = currentColumns.map(c => {
      const index = defaultColumns.findIndex(d => d.dataIndex === c.dataIndex);
      const def = defaultColumns[index];
      return {
        ...c,
        order: def?.order ?? index,
        visible: def?.visible ?? true,
      };
    });
    setColumns(reset);
  };

  const handleCancel = () => {
    setColumns(currentColumns);
    toggleVisibility();
  };

  const handleSave = () => {
    updateColumns(columns);
    toggleVisibility();
  };

  const sortedColumns = useMemo(() => {
    return [...columns].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [columns]);

  return (
    <Modal
      title={t("components.dataTable.columnsOrderModal.title")}
      open
      onCancel={handleCancel}
      footer={
        <footer className={s.footer} data-testid="columns-order-modal-footer">
          <Button type="dashed" onClick={handleDefault} className={s.btn}>
            {t("components.dataTable.defaultSettings")}
          </Button>
          <Button onClick={handleCancel} className={s.btn}>
            {t("components.buttons.cancel")}
          </Button>
          <Button type="primary" onClick={handleSave} className={s.btn}>
            {t("components.buttons.save")}
          </Button>
        </footer>
      }
    >
      <p className={s.description}>
        {t("components.dataTable.columnsOrderModal.description")}
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedColumns.map(col => col.id as string)}
          strategy={verticalListSortingStrategy}
        >
          {sortedColumns.map(col => (
            <SortableItem
              key={col.dataIndex}
              item={col}
              onToggle={handleToggleVisibility}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Modal>
  );
};
